import { useEffect, useState } from 'react';
import { Box, chakra, Flex, Text } from '@chakra-ui/react';
import { EditorContent, useEditor } from '@tiptap/react';
import { TextSelection } from '@tiptap/pm/state';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyleKit } from '@tiptap/extension-text-style';
import { executeAction } from '@/lattice/runtime';
import { usePortalUser } from '@/auth/roles';
import { EnhanceModal } from './AiField';
import {
  FONT_FAMILY_OPTIONS,
  FONT_SIZE_OPTIONS,
  isRichTextEmpty,
  plainTextToRichText,
  richTextToPlainText,
  sanitizeRichText,
} from '@/lib/rich-text';

type TiptapEditor = NonNullable<ReturnType<typeof useEditor>>;

/**
 * List and alignment are block (paragraph) properties — there's no such
 * thing as "half a paragraph" bulleted or centered. Left as-is, toggling
 * one of these against a selection that only partly covers its
 * paragraph(s) affects the *whole* paragraph, which reads as "it ignored
 * my selection" (unlike bold/italic, which are inline marks that already
 * scope correctly). To match how Word/Docs behave — only the selected
 * text becomes its own list item / gets aligned, the rest of the
 * paragraph is left alone — split the paragraph at the selection's edges
 * first so the selection exactly spans whole block(s), then run the
 * block command against that isolated selection.
 */
function runBlockCommand(editor: TiptapEditor, run: () => void) {
  const { state } = editor;
  const { $from, $to, empty } = state.selection;
  const needsStartSplit = !empty && $from.parentOffset > 0;
  const needsEndSplit = !empty && $to.parentOffset < $to.parent.content.size;

  if (needsStartSplit || needsEndSplit) {
    const origFrom = state.selection.from;
    const origTo = state.selection.to;
    const { tr } = state;

    // Split at the later position first so the earlier split's target
    // position isn't shifted by it.
    if (needsEndSplit) tr.split(origTo);
    if (needsStartSplit) tr.split(tr.mapping.map(origFrom, 1));

    // assoc 1 keeps `from` attached to the block that now starts there;
    // assoc -1 keeps `to` attached to the block that now ends there —
    // otherwise it can land just inside the following block instead.
    const from = tr.mapping.map(origFrom, 1);
    const to = tr.mapping.map(origTo, -1);
    tr.setSelection(TextSelection.create(tr.doc, from, to));
    editor.view.dispatch(tr);
  }

  run();
}

/**
 * Rich text editor for portal prose fields — bold/italic/underline, bullet
 * & numbered lists, alignment, font family/size. Stores sanitized HTML.
 * Mirrors AiField's props (label/value/onChange/baseline/context) so it
 * drops into the same call sites, including the "Enhance with AI" flow.
 */
export function RichTextField({
  label,
  value,
  onChange,
  context,
  placeholder,
  baseline,
}: {
  label?: string;
  value: string;
  onChange: (next: string) => void;
  /** What this text is, e.g. "a service card description" — steers the AI model. */
  context?: string;
  placeholder?: string;
  /** Published value to diff against — when it differs, the field is highlighted green. */
  baseline?: string;
}) {
  const user = usePortalUser();
  const [enhancing, setEnhancing] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        code: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        link: false,
        strike: false,
      }),
      TextStyleKit.configure({ color: false, backgroundColor: false, lineHeight: false }),
      TextAlign.configure({ types: ['paragraph'] }),
    ],
    content: value,
    onUpdate: ({ editor }) => onChange(sanitizeRichText(editor.getHTML())),
  });

  // Sync external value changes (Enhance apply, tier reorder reusing this
  // slot, draft reload) — but never while the user is actively typing here.
  useEffect(() => {
    if (!editor || editor.isFocused) return;
    const current = editor.getHTML();
    if (current !== value && !(isRichTextEmpty(current) && isRichTextEmpty(value))) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [value, editor]);

  const enhance = async () => {
    const plain = richTextToPlainText(value);
    if (!plain.trim() || enhancing) return;
    setEnhancing(true);
    setError(null);
    try {
      const result = await executeAction<{ enhanced: string }>(
        'ai-assist:enhanceText',
        { text: plain, context },
        user
      );
      setDraft(result.enhanced);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Enhancement failed');
    } finally {
      setEnhancing(false);
    }
  };

  const changed = baseline !== undefined && value !== baseline;
  const hasText = !isRichTextEmpty(value);

  if (!editor) return null;

  return (
    <Box w="100%">
      {label && (
        <Text fontSize="0.66rem" fontWeight="600" letterSpacing="0.14em" textTransform="uppercase" color="brand.ink" mb="0.35rem">
          {label}
        </Text>
      )}
      <Box
        border="1px solid"
        borderColor={changed ? '#8FBF3F' : 'paper.border'}
        boxShadow={changed ? '0 0 0 1px #B5E061' : undefined}
        borderRadius="8px"
        bg="paper.white"
        overflow="hidden"
      >
        <Toolbar editor={editor} onEnhance={() => void enhance()} enhancing={enhancing} disabled={!hasText} />
        <Box position="relative">
          <EditorContent editor={editor} className="rich-text-field-content" />
          {!hasText && placeholder && (
            <Text
              position="absolute"
              top="0.6rem"
              left="0.9rem"
              fontSize="0.85rem"
              color="ink.light"
              pointerEvents="none"
            >
              {placeholder}
            </Text>
          )}
        </Box>
      </Box>
      {error && (
        <Text fontSize="0.72rem" color="brand.terracotta" mt="0.3rem">
          {error}
        </Text>
      )}

      {draft !== null && (
        <EnhanceModal
          original={richTextToPlainText(value)}
          draft={draft}
          onDraftChange={setDraft}
          onApply={() => {
            onChange(sanitizeRichText(plainTextToRichText(draft)));
            setDraft(null);
          }}
          onCancel={() => setDraft(null)}
        />
      )}
    </Box>
  );
}

function ToolbarButton({
  onClick,
  active = false,
  disabled = false,
  title,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <chakra.button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active}
      disabled={disabled}
      onMouseDown={(event) => {
        // Keep focus (and the current selection) inside the editor.
        event.preventDefault();
      }}
      onClick={onClick}
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      minW="26px"
      h="26px"
      px="0.35rem"
      bg={active ? 'brand.wash' : 'transparent'}
      color={active ? 'brand.mid' : 'ink.body'}
      border="1px solid"
      borderColor={active ? 'brand.pale' : 'transparent'}
      borderRadius="6px"
      fontSize="0.78rem"
      cursor={disabled ? 'not-allowed' : 'pointer'}
      opacity={disabled ? 0.35 : 1}
      _hover={disabled ? undefined : { bg: 'brand.wash' }}
    >
      {children}
    </chakra.button>
  );
}

function ToolbarSelect({
  title,
  value,
  options,
  onChange,
}: {
  title: string;
  value: string;
  options: ReadonlyArray<{ label: string; value: string }>;
  onChange: (next: string) => void;
}) {
  return (
    <chakra.select
      title={title}
      aria-label={title}
      value={value}
      onMouseDown={(event) => event.stopPropagation()}
      onChange={(event) => onChange(event.target.value)}
      h="26px"
      px="0.3rem"
      fontSize="0.72rem"
      color="ink.body"
      bg="paper.white"
      border="1px solid"
      borderColor="paper.border"
      borderRadius="6px"
      cursor="pointer"
      maxW="9.5rem"
    >
      {options.map((option) => (
        <option key={option.label} value={option.value}>
          {option.label}
        </option>
      ))}
    </chakra.select>
  );
}

function Toolbar({
  editor,
  onEnhance,
  enhancing,
  disabled,
}: {
  editor: TiptapEditor;
  onEnhance: () => void;
  enhancing: boolean;
  disabled: boolean;
}) {
  // Re-render on every selection/transaction so active states stay live.
  const [, forceRender] = useState(0);
  useEffect(() => {
    const rerender = () => forceRender((n) => n + 1);
    editor.on('transaction', rerender);
    return () => {
      editor.off('transaction', rerender);
    };
  }, [editor]);

  return (
    <Flex
      align="center"
      gap="0.25rem"
      flexWrap="wrap"
      px="0.5rem"
      py="0.4rem"
      bg="paper.warm"
      borderBottom="1px solid"
      borderColor="paper.border"
    >
      <ToolbarButton title="Bold" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
        <Box as="strong">B</Box>
      </ToolbarButton>
      <ToolbarButton title="Italic" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}>
        <Box as="em">I</Box>
      </ToolbarButton>
      <ToolbarButton title="Underline" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}>
        <Box as="span" textDecoration="underline">U</Box>
      </ToolbarButton>

      <Divider />

      <ToolbarButton
        title="Bullet list"
        active={editor.isActive('bulletList')}
        onClick={() => runBlockCommand(editor, () => editor.chain().focus().toggleBulletList().run())}
      >
        •≡
      </ToolbarButton>
      <ToolbarButton
        title="Numbered list"
        active={editor.isActive('orderedList')}
        onClick={() => runBlockCommand(editor, () => editor.chain().focus().toggleOrderedList().run())}
      >
        1.≡
      </ToolbarButton>

      <Divider />

      <ToolbarButton
        title="Align left"
        active={editor.isActive({ textAlign: 'left' })}
        onClick={() => runBlockCommand(editor, () => editor.chain().focus().setTextAlign('left').run())}
      >
        ⟵
      </ToolbarButton>
      <ToolbarButton
        title="Align center"
        active={editor.isActive({ textAlign: 'center' })}
        onClick={() => runBlockCommand(editor, () => editor.chain().focus().setTextAlign('center').run())}
      >
        ↔
      </ToolbarButton>
      <ToolbarButton
        title="Align right"
        active={editor.isActive({ textAlign: 'right' })}
        onClick={() => runBlockCommand(editor, () => editor.chain().focus().setTextAlign('right').run())}
      >
        ⟶
      </ToolbarButton>
      <ToolbarButton
        title="Justify"
        active={editor.isActive({ textAlign: 'justify' })}
        onClick={() => runBlockCommand(editor, () => editor.chain().focus().setTextAlign('justify').run())}
      >
        ☰
      </ToolbarButton>

      <Divider />

      <ToolbarSelect
        title="Font family"
        value={(editor.getAttributes('textStyle').fontFamily as string) ?? ''}
        options={FONT_FAMILY_OPTIONS}
        onChange={(next) => {
          if (next) editor.chain().focus().setFontFamily(next).run();
          else editor.chain().focus().unsetFontFamily().run();
        }}
      />
      <ToolbarSelect
        title="Font size"
        value={(editor.getAttributes('textStyle').fontSize as string) ?? ''}
        options={FONT_SIZE_OPTIONS}
        onChange={(next) => {
          if (next) editor.chain().focus().setFontSize(next).run();
          else editor.chain().focus().unsetFontSize().run();
        }}
      />

      <Box flex="1" />

      <chakra.button
        type="button"
        title="Enhance with AI"
        aria-label="Enhance with AI"
        disabled={enhancing || disabled}
        onClick={onEnhance}
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        w="26px"
        h="26px"
        bg="transparent"
        border="none"
        borderRadius="full"
        fontSize="0.85rem"
        cursor="pointer"
        opacity={disabled ? 0.3 : enhancing ? 0.6 : 0.85}
        transition="opacity 0.15s, background 0.15s"
        _hover={enhancing || disabled ? {} : { opacity: 1, bg: 'brand.wash' }}
        animation={enhancing ? 'btnGreenFlash 1.4s ease-out infinite' : undefined}
      >
        ✨
      </chakra.button>
    </Flex>
  );
}

function Divider() {
  return <Box w="1px" h="18px" bg="paper.border" mx="0.15rem" />;
}
